import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://ritsu-api.mbek9606.workers.dev";

interface ProductVariant {
  id: number;
  variant_name: string;
  size_description: string;
  price_modifier: number;
  dimensions: string;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  description: string;
  category: 'canvas' | 'framed' | 'badge' | 'print';
  base_price: number;
  image_url: string | null;
  variants: ProductVariant[];
}

interface CartItem {
  product: Product;
  variant: ProductVariant;
  quantity: number;
  image_url: string;
}

interface Order {
  id: number;
  order_number: string;
  status: string;
  total_amount: number;
  shipping_cost: number;
  tax_amount: number;
  tracking_number: string | null;
  shipping_carrier: string | null;
  created_at: string;
  shipped_at: string | null;
  delivered_at: string | null;
  items: Array<{
    id: number;
    product_name: string;
    variant_name: string;
    quantity: number;
    unit_price: number;
    image_url: string;
  }>;
}

interface ShippingAddress {
  name: string;
  email: string;
  phone?: string;
  address: string;
  city: string;
  state?: string;
  postal_code: string;
  country: string;
}

interface StoreContextType {
  products: Product[];
  cart: CartItem[];
  orders: Order[];
  isLoading: boolean;
  error: string | null;
  fetchProducts: () => Promise<void>;
  fetchOrders: () => Promise<void>;
  addToCart: (product: Product, variant: ProductVariant, imageUrl: string) => void;
  removeFromCart: (productId: number, variantId: number) => void;
  updateCartQuantity: (productId: number, variantId: number, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  createOrder: (shipping: ShippingAddress) => Promise<{ orderId: number; orderNumber: string } | null>;
  completePayment: (orderId: number) => Promise<boolean>;
  getCategoryIcon: (category: string) => string;
  formatPrice: (cents: number) => string;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthToken = () => localStorage.getItem("auth_token");

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/store/products`);
      const data = await response.json() as { success: boolean; products: Product[]; error?: string };
      
      if (data.success) {
        setProducts(data.products);
      } else {
        throw new Error(data.error || "Failed to load products");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/store/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; orders: Order[]; error?: string };
      
      if (data.success) {
        setOrders(data.orders);
      }
    } catch (err) {
      console.error("Fetch orders error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addToCart = useCallback((product: Product, variant: ProductVariant, imageUrl: string) => {
    setCart((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.product.id === product.id && item.variant.id === variant.id
      );

      if (existingIndex >= 0) {
        const newCart = [...prev];
        newCart[existingIndex].quantity += 1;
        return newCart;
      }

      return [...prev, { product, variant, quantity: 1, image_url: imageUrl }];
    });
  }, []);

  const removeFromCart = useCallback((productId: number, variantId: number) => {
    setCart((prev) =>
      prev.filter((item) => !(item.product.id === productId && item.variant.id === variantId))
    );
  }, []);

  const updateCartQuantity = useCallback((productId: number, variantId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId, variantId);
      return;
    }

    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId && item.variant.id === variantId
          ? { ...item, quantity }
          : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce((total, item) => {
      const price = item.product.base_price + item.variant.price_modifier;
      return total + price * item.quantity;
    }, 0);
  }, [cart]);

  const createOrder = useCallback(async (shipping: ShippingAddress): Promise<{ orderId: number; orderNumber: string } | null> => {
    try {
      const token = getAuthToken();
      if (!token) {
        setError("Please sign in to place an order");
        return null;
      }

      const items = cart.map((item) => ({
        product_id: item.product.id,
        variant_id: item.variant.id,
        quantity: item.quantity,
        image_url: item.image_url,
      }));

      const response = await fetch(`${API_BASE_URL}/api/store/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items, shipping }),
      });

      const data = await response.json() as { success: boolean; order: { id: number; order_number: string }; error?: string };

      if (data.success) {
        clearCart();
        await fetchOrders();
        return { orderId: data.order.id, orderNumber: data.order.order_number };
      } else {
        throw new Error(data.error || "Failed to create order");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create order");
      return null;
    }
  }, [cart, clearCart, fetchOrders]);

  const completePayment = useCallback(async (orderId: number): Promise<boolean> => {
    try {
      const token = getAuthToken();
      if (!token) return false;

      const response = await fetch(`${API_BASE_URL}/api/store/orders/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId, payment_method: "card" }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        await fetchOrders();
        return true;
      } else {
        throw new Error(data.error || "Payment failed");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Payment failed");
      return false;
    }
  }, [fetchOrders]);

  const getCategoryIcon = useCallback((category: string) => {
    const icons: Record<string, string> = {
      canvas: "🎨",
      framed: "🖼️",
      badge: "🏷️",
      print: "📷",
    };
    return icons[category] || "📦";
  }, []);

  const formatPrice = useCallback((cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  }, []);

  return (
    <StoreContext.Provider
      value={{
        products,
        cart,
        orders,
        isLoading,
        error,
        fetchProducts,
        fetchOrders,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        clearCart,
        getCartTotal,
        createOrder,
        completePayment,
        getCategoryIcon,
        formatPrice,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
}